import { useEffect, useMemo, useState } from "react";
import {
  ensureProductsPricesCouponsPromotionCodes,
  ensureShippingRates,
  ensureTaxRates,
  ensureAppBuilder,
  ensureAppCheckout,
  ensureAppEntities,
  ensureAppPagesTemplatesIndex,
  ensureAppPagesTemplate,
  useModelQueryId,
  ensureMockUserDetails
} from "utils/hooks";
import { MOCK_RESOURCE } from "utils/constants/request";
import { useCtxReferences, useIsAuthed } from "utils/selectors";
import { useRouter } from "next/router";
import { authAndRedirect } from "utils/route";
import { getStorePageBuilder } from "utils/builder";
import { PAGE } from "utils/constants/models";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";
import { ENV } from "utils/constants/env";

export const ensureUnauthResources = ({
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes,
  fetchMerchantAccountTaxRates,
  fetchMerchantAccountShippingRates,
  fetchAppCheckout,
  fetchAppBuilder,
  fetchMockUserDetails,
  force
}) => {
  const { loading: loadingUser, user } = ensureMockUserDetails({
    fetchMockUserDetails
  });
  const { application, merchantAccount } = useCtxReferences();
  // - fetch checkout stubs like tax rates etc
  const { loading: loadingAppCheckout } = ensureAppCheckout({
    fetchAppCheckout
  });
  // - fetch stub collections of stripe records to sanitize / order the test records used within the builder
  const { loading: loadingAppBuilder } = ensureAppBuilder({ fetchAppBuilder });

  const commonReferences = {
    resourceID: user && user.id,
    resourceUUID: user && user.uuid,
    application,
    merchantAccount,
    /**
     * Need to explicitly pass test env to filter to test products / prices
     */
    env: ENV.TEST,
    force
  };

  const {
    loadingProductsPrices,
    loadingCouponsPromotionCodes,
    coupons,
    promotionCodes,
    products,
    prices
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes,
    ...commonReferences
  });

  const { loading: loadingTaxRates, taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates,
    ...commonReferences
  });
  const { loading: loadingShippingRates, shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates,
    ...commonReferences
  });

  const loading =
    loadingUser ||
    loadingProductsPrices ||
    loadingCouponsPromotionCodes ||
    loadingTaxRates ||
    loadingShippingRates ||
    loadingAppCheckout ||
    loadingAppBuilder;

  return {
    loading,
    application,
    merchantAccount,
    coupons,
    promotionCodes,
    products,
    prices,
    taxRates,
    shippingRates
  };
};

export const ensureUnauthInit = (props) => {
  const {
    loading,
    application,
    merchantAccount,
    coupons,
    promotionCodes,
    products,
    prices,
    taxRates,
    shippingRates
  } = ensureUnauthResources(props);
  /**
   * If the user is authenticated
   * - Prevent them from using the unauthenticated builder
   * - Redirect them to the Hub
   */
  const [authing, setAuthing] = useState(false);
  const isAuthed = useIsAuthed();
  const router = useRouter();
  useEffect(() => {
    if (isAuthed) {
      setAuthing(true);
      authAndRedirect({
        router,
        actions: {
          fetchUserDetails: props.fetchUserDetails
        },
        cb: () => setAuthing(false)
      });
    }
  }, [isAuthed]);
  const ctxLoading = loading || authing;
  const hasData = Boolean(products.length && application && merchantAccount);

  return {
    loading: ctxLoading,
    hasData,
    coupons,
    promotionCodes,
    products,
    prices,
    taxRates,
    shippingRates,
    application,
    merchantAccount
  };
};

/**
 * alias: page templates | template pages
 */
export const ensureUnauthPageBuilderInit = (props) => {
  const { loading: loadingUser } = ensureMockUserDetails({
    fetchMockUserDetails: props.fetchMockUserDetails
  });
  const { application, merchantAccount } = useCtxReferences();

  const [authing, setAuthing] = useState(false);
  const isAuthed = useIsAuthed();
  const router = useRouter();
  const templateUUID = useModelQueryId(PAGE);

  /**
   * Handle authed users
   * - Prevent them from using the unauthenticated builder
   * - Redirect them to the Hub
   */
  useEffect(() => {
    if (isAuthed) {
      setAuthing(true);
      authAndRedirect({
        router,
        actions: {
          fetchUserDetails: props.fetchUserDetails
        },
        cb: () => setAuthing(false)
      });
    }
  }, [isAuthed]);

  /**
   * Fetch Stripe resources
   */
  const {
    loading,
    coupons,
    promotionCodes,
    products,
    prices,
    taxRates,
    shippingRates
  } = ensureUnauthResources(props);

  /**
   * Fetch builder related resources
   */
  const {
    loading: loadingAppPagesTemplatesIndex,
    index
  } = ensureAppPagesTemplatesIndex({
    fetchAppPagesTemplatesIndex: props.fetchAppPagesTemplatesIndex,
    ...MOCK_RESOURCE
  });
  const { loading: loadingAppEntities, entities } = ensureAppEntities({
    fetchAppEntities: props.fetchAppEntities,
    ...MOCK_RESOURCE
  });
  const { loading: loadingTemplate, template } = ensureAppPagesTemplate({
    uuid: templateUUID,
    fetchAppPagesTemplate: props.fetchAppPagesTemplate,
    ...MOCK_RESOURCE
  });

  const storeData = getStorePageBuilder(templateUUID);
  const hasStoreData = !isEmpty(storeData);
  const hasTemplateData = !isEmpty(template);
  const hasAnyTemplateData = hasStoreData || hasTemplateData;

  /**
   * Prepare values with resource data
   */
  const values = useMemo(() => {
    let result = null;
    if (hasAnyTemplateData) {
      if (hasStoreData) {
        result = cloneDeep(storeData);
      } else if (hasTemplateData) {
        result = cloneDeep(template);
      }

      if (result) {
        result.manifest.data.data = {
          coupons,
          promotionCodes,
          products,
          prices,
          taxRates,
          shippingRates
        };
      }
    }
    return result;
  }, [hasStoreData, hasTemplateData]);

  /**
   * If there are no values then it's likely that the fetched template has the wrong uuid
   * - be careful when copying templates, easy to miss updating the template id too
   */
  const hasData = Boolean(
    hasAnyTemplateData &&
      products.length &&
      application &&
      merchantAccount &&
      !isEmpty(values)
  );

  const ctxLoading =
    loading ||
    authing ||
    loadingUser ||
    loadingAppEntities ||
    loadingAppPagesTemplatesIndex ||
    loadingTemplate;

  return {
    loading: ctxLoading,
    hasData,
    index,
    application,
    merchantAccount,
    template,
    entities,
    coupons,
    promotionCodes,
    products,
    prices,
    taxRates,
    shippingRates,
    values
  };
};
